import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Chip,
  Box,
  Modal,
  FormLabel,
  Grid,
  Divider,
  Typography,
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import './updateprofile.css';
import CloseIcon from '@mui/icons-material/Close';
import apis from '../../../Services/api-services';
import { toast } from 'react-toastify';
import AreasOfOperation from '../components/AreasOfOperation';
import { Add } from '@mui/icons-material';
import DropdownWithLabel from '../../../components/dropdownWithLabel';
import AgentProfilePicture from '../components/AgentProfilePicture';
import { useSelector } from 'react-redux';
import { RotatingLines } from 'react-loader-spinner';
import { colorsEnums } from '../../../constants/enums';
import { TRANSLATIONS } from '../../../Utils/translations';
import { useTranslation } from 'react-i18next';
import BrandDetails from '../CreateProfile/components/BrandDetails';

const UpdateProfile = ({
  open = false,
  onClose = () => {},
  agentDetails = {},
  getAgentDetails = () => {},
  updateAgentProfilePicture = () => {},
}) => {
  const { t } = useTranslation();
  const { userId } = useSelector((state) => state.userDetails || {});

  const parsedAreasOfOperation = JSON.parse(
    JSON.parse(agentDetails?.area_of_operation || null)
  );

  const [isDescriptionLoading, setIsDescriptionLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [dropdownValues, setDropdownValues] = useState([]);

  useEffect(() => {
    getStates();
  }, []);

  useEffect(() => {
    getAgentProfileDropdownValues();
  }, []);

  async function getStates() {
    const response = await apis.dropdown.getStates('IN');
    setStates(response?.data?.[0]?.states);
  }

  async function getCities(stateName) {
    let findState = states?.find((item) => item?.state_name === stateName);
    let stateCode = findState?.state_code;

    const response = await apis.dropdown.getCities('IN', stateCode);
    setCities(response?.data?.[0]?.cities);
  }

  async function getAgentProfileDropdownValues() {
    const response = await apis.agent.getAgentProfileDropdownValues();
    setDropdownValues(response?.data?.dropdowns);
  }

  const profileInitialValues = {
    name: agentDetails?.full_name || '',
    city: agentDetails?.city || '',
    state: agentDetails?.state || '',
    org_size: agentDetails?.org_size || '',
    description: agentDetails?.user_description || '',
    yearsOfExperience: agentDetails?.years_of_experience || '',
    specializations: agentDetails?.specialization || [],
    languages: agentDetails?.languages || [],
    areasOfOperation: parsedAreasOfOperation || [],
    companyName: agentDetails?.brand_name || '',
    otherServices: agentDetails?.other_services || [],
    instaPageLink: agentDetails?.instagram_link || '',
    fbPageLink: agentDetails?.facebook_link || '',
    linkedInPageLink: agentDetails?.linkedin_link || '',
    twitterPageLink: agentDetails?.twitter_link || '',
  };

  const validationSchema = Yup.object().shape({
    location: Yup.string(),
    yearsOfExperience: Yup.number()
      .min(0, 'Minimum value is 0')
      .max(100, 'Maximum value is 100'),
    companyName: Yup.string()
      .required('Display name is required')
      .min(3, 'Display name must be at least 3 characters'),
  });

  const handleFormSubmit = async (values) => {
    const {
      name,
      city,
      state,
      description,
      yearsOfExperience,
      specializations,
      languages,
      areasOfOperation,
      companyName,
      org_size,
      otherServices,
      instaPageLink,
      fbPageLink,
      linkedInPageLink,
      twitterPageLink,
    } = values || {};

    const filterAreasOfOperation = areasOfOperation.filter((item) => item.city);

    const params = {
      full_name: name,
      state,
      city,
      org_size,
      user_description: description,
      years_of_experience: yearsOfExperience || 0,
      languages: languages || [],
      area_of_operation: JSON.stringify(filterAreasOfOperation || {}) || [],
      specialization: specializations || [],
      other_services: otherServices || [],
      brand_name: companyName || '',
      instagram_link: instaPageLink || '',
      facebook_link: fbPageLink || '',
      linkedin_link: linkedInPageLink || '',
      twitter_link: twitterPageLink || '',
    };
    try {
      const response = await apis.agent.enhanceAgentProfile(params);

      if (response.data) {
        toast.success('Profile updated successfully');
        getAgentDetails();
        onClose();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update profile');
    }
  };

  const handleRegenerateDescription = async (setFieldValue) => {
    setIsDescriptionLoading(true);
    const parsedAreasOfOperation = JSON.parse(
      JSON.parse(agentDetails?.area_of_operation || null)
    );

    const params = {
      full_name: agentDetails?.full_name || '',
      specialization: agentDetails?.specialization || [],
      languages: agentDetails?.languages || [],
      area_of_operation: parsedAreasOfOperation || [],
      years_of_experience: agentDetails?.years_of_experience || 0,
      company_name: '',
    };

    const response = await apis.agent.regenerateAgentDescription(params);

    if (response?.data?.status === 'success') {
      setFieldValue('description', response?.data?.agent_description);
    }

    setIsDescriptionLoading(false);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        margin: '0 auto',
      }}
    >
      <Box
        sx={{
          background: '#fff',
          width: { lg: '68%', xs: '96%' },
          height: { lg: '80%', xs: '96%' },
          mt: { lg: '100px', xs: '0' },
          overflow: 'scroll',
          borderRadius: '10px',
          position: 'relative',
        }}
      >
        <Box sx={{ width: '100%', height: '6rem', background: '#F5EDFF' }} />

        <CloseIcon
          onClick={onClose}
          sx={{ position: 'absolute', top: 10, right: 10, cursor: 'pointer' }}
        />

        <Box sx={{ padding: { lg: '1rem 2rem', xs: '1rem' } }}>
          <AgentProfilePicture
            userId={userId}
            agentDetails={agentDetails}
            updateAgentProfilePicture={updateAgentProfilePicture}
          />

          <Formik
            enableReinitialize
            initialValues={profileInitialValues}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              setFieldValue,
              handleSubmit,
              handleBlur,
              isSubmitting,
            }) => (
              <Box
                sx={{
                  padding: { lg: '.5rem 1.5rem', xs: '.5rem' },
                  marginTop: { lg: '4rem', xs: '2rem' },
                }}
              >
                <Typography
                  sx={{ color: '#600DB4', fontSize: '1rem', fontWeight: '500' }}
                >
                  {t(TRANSLATIONS.TITLES.INFO)}{' '}
                </Typography>

                {/* Display Name */}
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  marginBottom={2}
                >
                  <Grid item lg={3} xs={12}>
                    <FormLabel
                      sx={{
                        color: '#000000',
                        fontSize: '15px',
                        fontWeight: '500',
                      }}
                    >
                      Display Name
                    </FormLabel>
                  </Grid>
                  <Grid item lg={9} xs={12}>
                    <TextField
                      fullWidth
                      name="companyName"
                      value={values.companyName}
                      onChange={handleChange}
                      placeholder="Ex: XYZ Pvt Ltd"
                      error={touched.companyName && Boolean(errors.companyName)}
                      helperText={touched.companyName && errors.companyName}
                    />
                  </Grid>
                </Grid>

                {/* Years of Experience */}
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  marginBottom={2}
                >
                  <Grid item lg={3} xs={12}>
                    <FormLabel
                      sx={{
                        color: '#000000',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      {t(TRANSLATIONS.TITLES.YEARS_OF_EXPERIENCE)}
                    </FormLabel>
                  </Grid>
                  <Grid item lg={9} xs={12}>
                    <TextField
                      fullWidth
                      name="yearsOfExperience"
                      value={values.yearsOfExperience}
                      onChange={handleChange}
                      placeholder={t(TRANSLATIONS.PARAGRAPHS.ENTER_YRS_OF_EXP)}
                      error={
                        touched.yearsOfExperience &&
                        Boolean(errors.yearsOfExperience)
                      }
                      helperText={
                        touched.yearsOfExperience && errors.yearsOfExperience
                      }
                    />
                  </Grid>
                </Grid>

                <Grid sx={{ margin: '2rem 0' }}>
                  <Divider />
                </Grid>

                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  marginBottom={2}
                  justifyContent="space-between"
                >
                  <Grid item lg={3} xs={12}>
                    <FormLabel
                      sx={{
                        color: '#000000',
                        fontSize: '15px',
                        fontWeight: '500',
                      }}
                    >
                      {t(TRANSLATIONS.TITLES.LOCATION)}
                    </FormLabel>
                  </Grid>
                  <Grid
                    item
                    lg={4.5}
                    xs={12}
                    sx={{ marginTop: { lg: '2rem', xs: 0 } }}
                  >
                    <DropdownWithLabel
                      lg={12}
                      hideLabel
                      name="state"
                      label={t(TRANSLATIONS.TITLES.STATE)}
                      value={values.state}
                      onChange={(e) => {
                        handleChange(e);
                        setFieldValue('state', e.target.value);
                        setFieldValue('city', '');
                        getCities(e.target.value);
                      }}
                      onBlur={handleBlur}
                      values={states?.map((state) => state.state_name)}
                      error={touched.state && Boolean(errors.state)}
                      touched={touched.state && Boolean(errors.state)}
                      helperText={touched.state && errors.state}
                    />
                  </Grid>
                  <Grid
                    item
                    lg={4.5}
                    xs={12}
                    sx={{ marginTop: { lg: '2rem', xs: 0 } }}
                  >
                    <DropdownWithLabel
                      lg={12}
                      hideLabel
                      name="city"
                      label={t(TRANSLATIONS.TITLES.CITY)}
                      value={values.city}
                      onChange={(e) => setFieldValue('city', e.target.value)}
                      onBlur={handleBlur}
                      values={cities?.map((city) => city.city_name)}
                      error={touched.city && Boolean(errors.city)}
                      touched={touched.city && Boolean(errors.city)}
                      helperText={touched.city && errors.city}
                    />
                  </Grid>
                </Grid>

                {/* Description Field */}
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  marginBottom={2}
                >
                  <Grid item lg={3} xs={12}>
                    <FormLabel
                      sx={{
                        color: '#000000',
                        fontSize: '15px',
                        fontWeight: '500',
                      }}
                    >
                      {t(TRANSLATIONS.TITLES.DESCRIPTION)}
                    </FormLabel>
                  </Grid>
                  <Grid item lg={9} xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={7}
                      name="description"
                      placeholder={t(TRANSLATIONS.TITLES.ENTER_DESCRIPTION)}
                      value={values.description}
                      onChange={handleChange}
                    />
                    <Button
                      variant="text"
                      sx={{
                        textTransform: 'capitalize',
                        textDecoration: 'underline',
                      }}
                      onClick={() => handleRegenerateDescription(setFieldValue)}
                    >
                      {isDescriptionLoading
                        ? t(TRANSLATIONS.TITLES.REGENERATING_DESCRIPTION)
                        : t(TRANSLATIONS.TITLES.REGENERATE)}

                      <RotatingLines
                        visible={isDescriptionLoading}
                        height="24"
                        width="24"
                        strokeColor="#600DB4"
                        strokeWidth="5"
                        animationDuration="0.7"
                      />
                    </Button>
                  </Grid>
                </Grid>

                <Grid sx={{ margin: '2rem 0' }}>
                  <Divider />
                </Grid>

                {/* Specializations */}
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  marginBottom={6}
                >
                  <Grid item lg={3} xs={12}>
                    <FormLabel
                      sx={{
                        color: '#000000',
                        fontSize: '15px',
                        fontWeight: '500',
                      }}
                    >
                      {t(TRANSLATIONS.SUB_TITLES.MY_SPECIALIZATION)}
                    </FormLabel>
                  </Grid>
                  <Grid item lg={9} xs={12}>
                    {dropdownValues?.specializations?.map((specialization) => (
                      <Chip
                        key={specialization.id}
                        label={specialization.value}
                        clickable
                        onClick={() => {
                          const currentSelection = values.specializations;
                          if (currentSelection.includes(specialization)) {
                            setFieldValue(
                              'specializations',
                              currentSelection.filter(
                                (item) => item !== specialization.value
                              )
                            );
                          } else {
                            setFieldValue('specializations', [
                              ...currentSelection,
                              specialization.value,
                            ]);
                          }
                        }}
                        sx={{
                          backgroundColor: values.specializations.includes(
                            specialization.value
                          )
                            ? '#F5EDFF'
                            : '#fff',
                          border: '1px solid #DCBCFAE5',
                          color: '#600DB4',
                          margin: '8px',
                          borderRadius: '8px',
                          padding: '6px 2px',
                          fontSize: { lg: '16px', xs: '14px' },
                          '&:hover': {
                            backgroundColor: values.specializations.includes(
                              specialization.value
                            )
                              ? '#E0D0FF'
                              : 'grey.400',
                          },
                        }}
                      />
                    ))}
                    {touched.specializations &&
                      Boolean(errors.specializations) && (
                        <div
                          style={{
                            color: colorsEnums.error,
                            fontSize: '16px',
                          }}
                        >
                          {errors.specializations}
                        </div>
                      )}
                  </Grid>
                </Grid>

                {/* Other Services */}
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  marginBottom={2}
                >
                  <Grid item lg={3} xs={12}>
                    <FormLabel
                      sx={{
                        color: '#000000',
                        fontSize: '15px',
                        fontWeight: '500',
                      }}
                    >
                      {t(TRANSLATIONS.TITLES.OTHER_SERVICES)}
                    </FormLabel>
                  </Grid>
                  <Grid item lg={9} xs={12}>
                    {dropdownValues?.other_services?.map((service) => (
                      <Chip
                        key={service.id}
                        label={service.value}
                        clickable
                        onClick={() => {
                          const currentSelection = values.otherServices;
                          if (currentSelection.includes(service.value)) {
                            setFieldValue(
                              'otherServices',
                              currentSelection.filter(
                                (item) => item !== service.value
                              )
                            );
                          } else {
                            setFieldValue('otherServices', [
                              ...currentSelection,
                              service.value,
                            ]);
                          }
                        }}
                        sx={{
                          backgroundColor: values.otherServices.includes(
                            service.value
                          )
                            ? '#F5EDFF'
                            : '#fff',
                          border: '1px solid #DCBCFAE5',
                          color: '#600DB4',
                          margin: '8px',
                          borderRadius: '8px',
                          padding: '6px 2px',
                          fontSize: { lg: '16px', xs: '14px' },
                          '&:hover': {
                            backgroundColor: values.otherServices.includes(
                              service.value
                            )
                              ? '#E0D0FF'
                              : 'grey.400',
                          },
                        }}
                      />
                    ))}
                    {touched.otherServices && Boolean(errors.otherServices) && (
                      <div
                        style={{
                          color: colorsEnums.error,
                          fontSize: '0.75rem',
                        }}
                      >
                        {errors.otherServices}
                      </div>
                    )}
                  </Grid>
                </Grid>

                <Grid sx={{ margin: '2rem 0' }}>
                  <Divider />
                </Grid>

                {/* Languages */}
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  marginBottom={2}
                >
                  <Grid item lg={3} xs={12}>
                    <FormLabel
                      sx={{
                        color: '#000000',
                        fontSize: '15px',
                        fontWeight: '500',
                      }}
                    >
                      {t(TRANSLATIONS.TITLES.LANGUAGE_S)}
                    </FormLabel>
                  </Grid>
                  <Grid item lg={9} xs={12}>
                    {[
                      'English',
                      'Hindi',
                      'Kannada',
                      'Tamil',
                      'Telugu',
                      'Malayalam',
                      'Bangla',
                    ].map((language) => (
                      <Chip
                        key={language}
                        label={language}
                        clickable
                        onClick={() => {
                          const currentSelection = values.languages;
                          if (currentSelection.includes(language)) {
                            setFieldValue(
                              'languages',
                              currentSelection.filter(
                                (item) => item !== language
                              )
                            );
                          } else {
                            setFieldValue('languages', [
                              ...currentSelection,
                              language,
                            ]);
                          }
                        }}
                        sx={{
                          backgroundColor: values.languages.includes(language)
                            ? '#F5EDFF'
                            : '#fff',
                          border: '1px solid #DCBCFAE5',
                          color: '#600DB4',
                          margin: '8px',
                          borderRadius: '8px',
                          padding: '6px 2px',
                          fontSize: { lg: '16px', xs: '14px' },
                          '&:hover': {
                            backgroundColor: values.languages.includes(language)
                              ? '#E0D0FF'
                              : 'grey.400', // Optional hover effect
                          },
                        }}
                      />
                    ))}
                    {touched.languages && Boolean(errors.languages) && (
                      <div
                        style={{
                          color: colorsEnums.error,
                          fontSize: '0.75rem',
                        }}
                      >
                        {errors.languages}
                      </div>
                    )}
                  </Grid>
                </Grid>

                <Grid sx={{ margin: '2rem 0' }}>
                  <Divider />
                </Grid>

                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  marginBottom={2}
                >
                  <Grid item lg={3} xs={12}>
                    <FormLabel
                      sx={{
                        color: '#000000',
                        fontSize: '15px',
                        fontWeight: '500',
                      }}
                    >
                      {t(TRANSLATIONS.TITLES.ORG_SIZE)}
                    </FormLabel>
                  </Grid>
                  <Grid item lg={9} xs={12}>
                    {dropdownValues?.org_size?.map((option) => (
                      <Chip
                        key={option.id}
                        label={option.value}
                        clickable
                        onClick={() => {
                          const currentSelection = values.org_size;
                          if (currentSelection !== option.value) {
                            setFieldValue('org_size', option.value);
                          }
                        }}
                        sx={{
                          backgroundColor:
                            values.org_size === option.value
                              ? '#F5EDFF'
                              : '#fff',
                          border: '1px solid #DCBCFAE5',
                          color: '#600DB4',
                          margin: '8px',
                          borderRadius: '8px',
                          padding: '6px 2px',
                          fontSize: { lg: '16px', xs: '14px' },
                          '&:hover': {
                            backgroundColor:
                              values.org_size === option.value
                                ? '#E0D0FF'
                                : 'grey.400',
                          },
                        }}
                      />
                    ))}

                    {touched.org_size && Boolean(errors.org_size) && (
                      <div
                        style={{
                          color: colorsEnums.error,
                          fontSize: '0.75rem',
                        }}
                      >
                        {errors.org_size}
                      </div>
                    )}
                  </Grid>
                </Grid>

                <Grid sx={{ margin: '2rem 0' }}>
                  <Divider />
                </Grid>
                {/* Location Dropdown */}
                <Grid container spacing={2} marginBottom={2}>
                  <Grid item lg={3} xs={12}>
                    <Typography
                      sx={{
                        color: '#600DB4',
                        fontSize: '1rem',
                        fontWeight: '500',
                        marginBottom: '1rem',
                      }}
                    >
                      {t(TRANSLATIONS.TITLES.INFO)}
                    </Typography>

                    <FormLabel
                      sx={{
                        color: '#000000',
                        fontSize: '15px',
                        fontWeight: '500',
                      }}
                    >
                      {t(TRANSLATIONS.TITLES.LOCATION)}
                    </FormLabel>
                  </Grid>
                  <Grid item lg={9} xs={12}>
                    {values?.areasOfOperation?.map((area, index) => (
                      <AreasOfOperation
                        key={index}
                        hideLabel
                        areasOfOperation={values.areasOfOperation}
                        area={area}
                        index={index}
                        setFieldValue={setFieldValue}
                        dropdownValues={dropdownValues}
                        style={{}}
                      />
                    ))}

                    {values.areasOfOperation.length <
                      dropdownValues?.cities?.length && (
                      <Button
                        sx={{
                          background: '#F5EDFF',
                          color: '#7B0DB4',
                          textTransform: 'capitalize',
                          gap: '.2rem',
                          borderRadius: '8px',
                        }}
                        onClick={() => {
                          let newArr = [
                            ...values.areasOfOperation,
                            {
                              city: '',
                              places: '',
                            },
                          ];
                          setFieldValue('areasOfOperation', newArr);
                        }}
                      >
                        <Add sx={{ fontSize: '18px' }} />
                        {t(TRANSLATIONS.SUB_TITLES.ADD_ANOTHER_CITY)}
                      </Button>
                    )}
                  </Grid>
                </Grid>

                <Grid sx={{ margin: '2rem 0' }}>
                  <Divider />
                </Grid>

                {/* s */}
                <BrandDetails
                  values={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  setFieldValue={setFieldValue}
                />

                <Box
                  sx={{
                    textAlign: 'right',
                    display: 'flex',
                    columnGap: 2,
                    justifyContent: 'flex-end',
                    marginBottom: '2rem',
                    marginTop: '12px',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                      border: '1.2px solid #D2D2D2',
                      color: '#000',
                      textTransform: 'capitalize',
                    }}
                  >
                    {t(TRANSLATIONS.TITLES.CANCEL)}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    sx={{
                      textTransform: 'capitalize',
                      columnGap: '4px',
                      borderRadius: '8px',
                    }}
                  >
                    {isSubmitting
                      ? t(TRANSLATIONS.TITLES.SAVING_CHANGES)
                      : t(TRANSLATIONS.TITLES.SAVE)}
                    <RotatingLines
                      visible={isDescriptionLoading}
                      height="24"
                      width="24"
                      strokeColor="#fff"
                      strokeWidth="5"
                      animationDuration="0.7"
                    />
                  </Button>
                </Box>
              </Box>
            )}
          </Formik>
        </Box>
      </Box>
    </Modal>
  );
};

export default UpdateProfile;
